import {
  GarageSurcharge,
  Surcharge,
  SurchargeSurchargeTypeEnum,
  SurchargeSurchargeCategoryEnum,
} from '@parkingbase/ecommerce-api-dev';

/** A display line for a single surcharge: its name and its dollar amount. */
export interface SurchargeRow {
  label: string;
  amount: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Computes the processing-fee surcharge for an upcoming charge of `total`,
 * given the garage's surcharge config and the surcharge already paid on the
 * ticket (if any).
 *
 * Mirrors the legacy reservation app's `getProcessingFeeFromSurcharge` so
 * front-end displays match across the two apps. The server still applies the
 * surcharge — this is for display only.
 *
 * - FLAT: returns `surchargeAmount` once. Returns 0 if a flat surcharge has
 *   already been paid (`paidSurcharge > 0`).
 * - PERCENT: returns `surchargePercent`% of `total`, rounded to 2 decimals.
 * - FLAT_AND_PERCENT: returns flat + percent. The flat portion is suppressed
 *   if already paid.
 */
export function computeProcessingFee(
  surcharge?: Surcharge | null,
  total?: number,
  paidSurcharge: number = 0
): number {
  if (!surcharge) return 0;

  const { surchargeType, surchargeAmount, surchargePercent } = surcharge;

  if (surchargeType === SurchargeSurchargeTypeEnum.FLAT) {
    if (paidSurcharge > 0) return 0;
    return surchargeAmount ?? 0;
  }

  if (surchargeType === SurchargeSurchargeTypeEnum.PERCENT) {
    // PB-3894: the percentage lives in `surchargeAmount`, NOT `surchargePercent`.
    //
    // ## Invariants:
    // - PERCENT reads `surchargeAmount`. Why: that is what the server charges
    //   (CreditCardHelper.getCreditCardProcessingFee case PERCENT) and what the legacy app
    //   displays. Real garages store the rate there and leave `surchargePercent` at 0, so
    //   reading the intuitively-named field showed a $0 fee while the customer was charged
    //   the real one — the wallet sheet then authorised less than we took.
    // - Only FLAT_AND_PERCENT uses `surchargePercent`, for its percent half.
    const percentRate = surchargeAmount ?? 0;
    if (!percentRate || !total) return 0;
    return Math.round(total * (percentRate / 100) * 100) / 100;
  }

  if (surchargeType === SurchargeSurchargeTypeEnum.FLAT_AND_PERCENT) {
    const percent =
      surchargePercent && total ? Math.round(total * (surchargePercent / 100) * 100) / 100 : 0;
    if (paidSurcharge > 0) return percent;
    return (surchargeAmount ?? 0) + percent;
  }

  return 0;
}

/**
 * Human-readable display value for a garage's surcharge, used in the
 * "…processing fee applies to card payments" note (PB-3458). Fee-type aware so
 * the note is correct for every garage — a hardcoded percentage would be wrong
 * on a FLAT-fee garage. Mirrors the legacy reservation app's surcharge-string
 * derivation (`AutoPayEdit.renderProcessingFee`).
 *
 * - PERCENT: `"3%"`
 * - FLAT: `"$5.00"`
 * - FLAT_AND_PERCENT: `"$5.00 + 3%"`
 * - NONE / missing / no configured value: `""` (caller hides the note)
 *
 * @param surcharge the garage's configured surcharge, or null/undefined
 * @param currencySymbol currency prefix for the flat portion (default `"$"`)
 * @returns the display string, or `""` when no fee is configured
 */
export function formatSurchargeDisplayValue(
  surcharge?: Surcharge | null,
  currencySymbol: string = '$'
): string {
  if (!surcharge) return '';

  const { surchargeType, surchargeAmount, surchargePercent } = surcharge;
  const flat =
    surchargeAmount != null && surchargeAmount > 0
      ? `${currencySymbol}${surchargeAmount.toFixed(2)}`
      : '';
  const percent = surchargePercent != null && surchargePercent > 0 ? `${surchargePercent}%` : '';

  if (surchargeType === SurchargeSurchargeTypeEnum.FLAT) return flat;
  // PB-3894: a PERCENT surcharge stores its rate in `surchargeAmount` (see computeProcessingFee),
  // so rendering `surchargePercent` here printed an empty label for every such garage.
  if (surchargeType === SurchargeSurchargeTypeEnum.PERCENT) {
    return surchargeAmount != null && surchargeAmount > 0 ? `${surchargeAmount}%` : '';
  }
  if (surchargeType === SurchargeSurchargeTypeEnum.FLAT_AND_PERCENT) {
    return [flat, percent].filter(Boolean).join(' + ');
  }
  return '';
}

/**
 * The surcharges configured for a category — mirrors the api's
 * `GarageSurcharge.getSurchargesForCategory` (the multi-surcharge `surcharges[]` array, falling
 * back to the single legacy field when the array is empty).
 *
 * A garage can configure MORE THAN ONE surcharge per category (each with its own name and amount);
 * this returns every one that should be billed to — and displayed on the billing info of — the
 * account. PB-2304: `accountType` is a GL label, not a filter, so every configured surcharge in the
 * category applies regardless of account type. `NONE`-typed surcharges are excluded (no fee).
 *
 * @param garageSurcharge the garage's surcharge configuration, or null/undefined
 * @param category the surcharge category (e.g. MONTHLY_RESERVATION, MONTHLY_PAYMENT)
 * @returns the applicable surcharges, in configured order (never null)
 */
export function getMatchingSurcharges(
  garageSurcharge: GarageSurcharge | undefined | null,
  category: SurchargeSurchargeCategoryEnum
): Surcharge[] {
  if (!garageSurcharge) return [];
  const inCategory = (garageSurcharge.surcharges ?? []).filter(
    (s) => String(s.surchargeCategory) === String(category)
  );
  const legacyField =
    category === SurchargeSurchargeCategoryEnum.MONTHLY_PAYMENT
      ? garageSurcharge.monthlyPaymentProcessingFee
      : garageSurcharge.monthlyReservationProcessingFee;
  const list = inCategory.length ? inCategory : legacyField ? [legacyField] : [];
  return list.filter((s) => s.surchargeType && s.surchargeType !== SurchargeSurchargeTypeEnum.NONE);
}

/**
 * Builds one billing-info display line per configured surcharge, reconciled to the
 * server's authoritative total.
 *
 * The server returns a single aggregate processing-fee total (it does not break the fee
 * down per surcharge), so this splits that authoritative `serverTotal` across the matching
 * surcharges the SAME way the api's `createSurchargeInvoiceForCategory` splits the charged
 * fee across its invoice line items — proportionally by each surcharge's computed weight,
 * with the last line absorbing any rounding remainder. That guarantees the itemized lines
 * always sum exactly to the total shown.
 *
 * - 0 surcharges → `[]` (caller renders nothing).
 * - 1 surcharge → one line at the exact `serverTotal`, labelled `singleLabel` so the common
 *   single-surcharge display is unchanged.
 * - N surcharges → one line each, labelled by the surcharge's `customName` (or `fallbackLabel`).
 *
 * @param surcharges the applicable surcharges (from {@link getMatchingSurcharges})
 * @param serverTotal the authoritative aggregate processing fee the lines must sum to
 * @param base the surchargeable base, used only to weight the multi-surcharge split
 * @param singleLabel label for the one-surcharge case (preserves the existing display)
 * @param fallbackLabel label for a surcharge with no `customName`
 * @returns display rows summing to `serverTotal`
 */
export function buildSurchargeRows(
  surcharges: Surcharge[] | undefined | null,
  serverTotal: number,
  base: number,
  singleLabel: string,
  fallbackLabel: string
): SurchargeRow[] {
  const list = (surcharges ?? []).filter(Boolean);
  if (!list.length || !serverTotal || serverTotal <= 0) return [];
  if (list.length === 1) {
    return [{ label: singleLabel, amount: round2(serverTotal) }];
  }
  const weights = list.map((s) => computeProcessingFee(s, base));
  const sumWeights = weights.reduce((a, b) => a + b, 0);
  const rows: SurchargeRow[] = list.map((s, i) => ({
    label: s.customName || fallbackLabel,
    amount:
      sumWeights > 0
        ? round2(serverTotal * (weights[i] / sumWeights))
        : round2(serverTotal / list.length),
  }));
  const summed = round2(rows.reduce((a, r) => a + r.amount, 0));
  const remainder = round2(serverTotal - summed);
  if (remainder !== 0) {
    const last = rows[rows.length - 1];
    last.amount = round2(last.amount + remainder);
  }
  return rows;
}

/**
 * Builds one billing-info display line per configured surcharge for the client-computed
 * paths (invoice payment, buy-pass) — where the frontend, not a server quote, is the
 * source of the fee amount. Each line's amount is computed directly from its surcharge via
 * {@link computeProcessingFee}, so the total is the sum of the lines.
 *
 * - 0 surcharges → `[]`.
 * - 1 surcharge → one line labelled `singleLabel` (preserves the existing single-line display).
 * - N surcharges → one line each, labelled by `customName` (or `fallbackLabel`).
 *
 * Zero-amount lines are dropped (e.g. a NONE-type or already-paid flat surcharge).
 *
 * @param surcharges the applicable surcharges (from {@link getMatchingSurcharges})
 * @param base the amount the surcharge is computed against
 * @param singleLabel label for the one-surcharge case
 * @param fallbackLabel label for a surcharge with no `customName`
 * @returns display rows, one per non-zero surcharge
 */
export function computeSurchargeRows(
  surcharges: Surcharge[] | undefined | null,
  base: number,
  singleLabel: string,
  fallbackLabel: string
): SurchargeRow[] {
  const list = (surcharges ?? []).filter(Boolean);
  return list
    .map((s) => ({
      label: list.length > 1 ? s.customName || fallbackLabel : singleLabel,
      amount: round2(computeProcessingFee(s, base)),
    }))
    .filter((r) => r.amount > 0);
}

/**
 * Sums the fee of every surcharge against `base`, rounding each one first — the same
 * arithmetic as the api's `CreditCardHelper.getProcessingFeeTotal`, so a client-computed
 * total ties out to what the server charges for a multi-surcharge garage (PB-2304).
 *
 * @param surcharges the applicable surcharges (from {@link getMatchingSurcharges})
 * @param base the amount the surcharges are computed against
 * @returns the combined fee, rounded to two decimals (0 when none apply)
 */
export function sumSurchargeFees(surcharges: Surcharge[] | undefined | null, base: number): number {
  return round2(
    (surcharges ?? []).reduce((sum, s) => sum + round2(computeProcessingFee(s, base)), 0)
  );
}

/**
 * The "…processing fee applies" note value for every applicable surcharge, joined with
 * `" + "` (e.g. `"3% + $1.00"`), so a multi-surcharge garage discloses each fee rather
 * than only the first (PB-2304). A single surcharge reads exactly as
 * {@link formatSurchargeDisplayValue}.
 *
 * @param surcharges the applicable surcharges (from {@link getMatchingSurcharges})
 * @param currencySymbol currency prefix for flat portions (default `"$"`)
 * @returns the display string, or `""` when no surcharge has a displayable value
 */
export function formatSurchargesDisplayValue(
  surcharges: Surcharge[] | undefined | null,
  currencySymbol: string = '$'
): string {
  return (surcharges ?? [])
    .map((s) => formatSurchargeDisplayValue(s, currencySymbol))
    .filter(Boolean)
    .join(' + ');
}

/** The api sends `PricingConfig` surcharge types as strings; this maps them to the client enum. */
const PRICING_SURCHARGE_TYPE_MAP: Record<string, SurchargeSurchargeTypeEnum> = {
  FLAT: SurchargeSurchargeTypeEnum.FLAT,
  PERCENT: SurchargeSurchargeTypeEnum.PERCENT,
  FLAT_AND_PERCENT: SurchargeSurchargeTypeEnum.FLAT_AND_PERCENT,
};

/**
 * The signup's applicable surcharges as `Surcharge`s, read from the garage-search
 * `PricingConfig` (PB-2304).
 *
 * ## Invariants:
 * - Falls back to the flattened primary fields when `surcharges` is absent. Why: the api
 *   (`GarageResultAssembler.buildMonthlyCategoryPricingConfig`) sends `surcharges` ONLY when more
 *   than one surcharge applies — a single-surcharge garage carries it in the primary fields alone,
 *   so reading only the list would drop that garage's fee entirely.
 * - Unknown / `NONE` types are dropped. Why: they carry no fee, and the api excludes them from
 *   the charge.
 *
 * @param pricing the garage's monthly `PricingConfig`, or null/undefined
 * @returns the applicable surcharges, in configured order (never null)
 */
export function pricingConfigToSurcharges(
  pricing:
    | {
        surchargeType?: string;
        surchargeAmount?: number;
        surchargePercent?: number;
        surchargeName?: string;
        surcharges?: Array<{
          surchargeType?: string;
          surchargeAmount?: number;
          surchargePercent?: number;
          customName?: string;
        }>;
      }
    | undefined
    | null
): Surcharge[] {
  if (!pricing) return [];
  const raw = pricing.surcharges?.length
    ? pricing.surcharges
    : [{ ...pricing, customName: pricing.surchargeName }];
  return raw.flatMap((s): Surcharge[] => {
    const surchargeType = s.surchargeType ? PRICING_SURCHARGE_TYPE_MAP[s.surchargeType] : undefined;
    if (!surchargeType) return [];
    return [
      {
        surchargeType,
        surchargeAmount: s.surchargeAmount,
        surchargePercent: s.surchargePercent,
        customName: s.customName,
      },
    ];
  });
}

/**
 * Processing fee from the reserve app's flattened surcharge fields (the string
 * `surchargeType` on `PricingConfig`). Maps the string to the `Surcharge` enum
 * and delegates to {@link computeProcessingFee} so both paths share one formula.
 */
export function computeProcessingFeeFromPricing(
  pricing:
    | { surchargeType?: string; surchargeAmount?: number; surchargePercent?: number }
    | undefined
    | null,
  total: number
): number {
  const surchargeType = pricing?.surchargeType
    ? PRICING_SURCHARGE_TYPE_MAP[pricing.surchargeType]
    : undefined;
  if (!surchargeType) return 0;
  return computeProcessingFee(
    {
      surchargeType,
      surchargeAmount: pricing?.surchargeAmount,
      surchargePercent: pricing?.surchargePercent,
    },
    total
  );
}
